import React from 'react'
import PropTypes from 'prop-types'
import { Mail, Building2, Shield, CheckCircle } from 'lucide-react'
import ProfileAvatar from './ProfileAvatar'
import { Badge } from '@components/common'
import './ProfileCard.css'

/**
 * Profile Card Component
 *
 * Left sidebar card displaying user avatar, name, role, and key info.
 * Handles avatar upload/remove with visual feedback.
 *
 * @param {Object} props - Component props
 * @param {Object} props.user - User data object
 * @param {Function} props.onAvatarUpload - Avatar upload handler
 * @param {Function} props.onAvatarRemove - Avatar remove handler
 * @param {boolean} props.avatarLoading - Avatar upload loading state
 */
const ProfileCard = ({
	user,
	onAvatarUpload,
	onAvatarRemove,
	avatarLoading = false,
}) => {
	if (!user) return null

	const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
	const avatarUrl = user.avatar
		? `${import.meta.env.VITE_API_BASE_URL?.replace('/api', '')}${user.avatar}`
		: null

	// Status badge variant mapping
	const statusVariants = {
		active: 'success',
		inactive: 'secondary',
		suspended: 'danger',
	}

	return (
		<div className="profile-card">
			{/* Avatar Section */}
			<div className="profile-card__avatar-section">
				<ProfileAvatar
					src={avatarUrl}
					name={fullName}
					onUpload={onAvatarUpload}
					onRemove={onAvatarRemove}
					loading={avatarLoading}
					size="large"
				/>
			</div>

			{/* Name and Role */}
			<div className="profile-card__identity">
				<h2 className="profile-card__name">{fullName || 'User'}</h2>
				<div className="profile-card__role">
					<Shield size={14} />
					<span>{user.role_name || 'No Role'}</span>
				</div>
			</div>

			{/* Quick Info */}
			<div className="profile-card__info">
				<div className="profile-card__info-item">
					<Mail size={16} className="profile-card__info-icon" />
					<span className="profile-card__info-text">{user.email}</span>
				</div>

				{user.company_name && (
					<div className="profile-card__info-item">
						<Building2 size={16} className="profile-card__info-icon" />
						<span className="profile-card__info-text">{user.company_name}</span>
					</div>
				)}
			</div>

			{/* Status Badges */}
			<div className="profile-card__badges">
				<Badge variant={statusVariants[user.status] || 'secondary'}>
					{user.status?.charAt(0).toUpperCase() + user.status?.slice(1) || 'Unknown'}
				</Badge>

				{user.email_verified_at && (
					<Badge variant="success" icon={<CheckCircle size={12} />}>
						Verified
					</Badge>
				)}
			</div>
		</div>
	)
}

ProfileCard.propTypes = {
	user: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		first_name: PropTypes.string,
		last_name: PropTypes.string,
		email: PropTypes.string,
		avatar: PropTypes.string,
		status: PropTypes.string,
		role_name: PropTypes.string,
		company_name: PropTypes.string,
		email_verified_at: PropTypes.string,
	}),
	onAvatarUpload: PropTypes.func,
	onAvatarRemove: PropTypes.func,
	avatarLoading: PropTypes.bool,
}

export default ProfileCard
